import { createSignal, createEffect, createResource } from 'solid-js';
import { useApi, isOk } from '../api';
import type { UserGrantSummaryDto } from '../services/grantsApi';

const [trigger, setTrigger] = createSignal(0);
let cached: UserGrantSummaryDto | null = null;
let cachedLicensedBotTypes: string[] | null = null;

export function refreshLicenses() { cached = null; cachedLicensedBotTypes = null; setTrigger(t => t + 1); }

function useGrantSummaryResource() {
    const api = useApi();
    return createResource(trigger, async () => {
        if (cached) return cached;
        const res = await api.grants.me();
        if (isOk(res.data)) {
            cached = res.data.value as UserGrantSummaryDto;
            return cached;
        }
        return null;
    });
}

// Bot license types are open to users who hold none themselves but operate a bot someone else licensed.
function useLicensedBotTypesResource() {
    const api = useApi();
    return createResource(trigger, async () => {
        if (cachedLicensedBotTypes) return cachedLicensedBotTypes;
        const res = await api.botLicense.me();
        cachedLicensedBotTypes = isOk(res.data) ? res.data.value.licensedTypesOnAccessibleBots : [];
        return cachedLicensedBotTypes;
    });
}

export function useLicense(type: string): () => boolean {
    const [resource] = useGrantSummaryResource();
    const [licensedBotTypes] = useLicensedBotTypesResource();
    const [enabled, setEnabled] = createSignal(false);
    createEffect(() => {
        const summary = resource();
        if (!summary) { setEnabled(false); return; }
        const entry = summary.entries.find(e => e.type === type);
        if (!entry) { setEnabled(false); return; }
        if (entry.kind === 'Seat') {
            setEnabled(entry.effectiveValue > 0 || (licensedBotTypes() ?? []).includes(type));
            return;
        }
        setEnabled(entry.effectiveValue > 0);
    });
    return enabled;
}

export function useGrantSummary(): () => UserGrantSummaryDto | null {
    const [resource] = useGrantSummaryResource();
    const [val, setVal] = createSignal<UserGrantSummaryDto | null>(null);
    createEffect(() => {
        const v = resource();
        setVal(v ?? null);
    });
    return val;
}
